/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useSession } from '@marriott/mi-headless-utils';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import {
  FILTER_TYPES,
  FILTERS_MAPPING,
  LOCATION_KEYS,
  LOCATIONS,
  PROPERTIES_LIMIT,
  REGION_FILTER,
} from './constants/constant';
import { ActivitiesInput, DisplayedFilter } from '../organisms/SearchResultsComponent/SearchResultsComponent.types';
import dayjs from 'dayjs';

declare global {
  interface Window {
    alloy: Alloy;
    _satellite: Record<string, any>;
  }
}
export type Alloy = {
  <T = void>(event: AdobeWebSDKCommand, opt1?: unknown): Promise<T>;
};

type AdobeWebSDKCommand =
  | 'configure'
  | 'event'
  | 'setConsent'
  | 'getIdentity'
  | 'appendIdentityToUrl'
  | 'syncIdentity'
  | 'getLibraryInfo'
  | 'sendEvent'
  | 'setDebug'
  | 'getDebug'
  | 'getConfig'
  | 'getPropositions'
  | 'applyPropositions'
  | 'sendPropositionInteraction'
  | 'getDecisionScopes'
  | 'getDecisions'
  | 'applyDecisions'
  | 'sendEventWithPropositions';

interface ResultType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propositions?: any;
}

export type dataLayerType = Record<string, unknown>;

export type pageViewACDLkeyGeneratorType =
  | {
      datalayer?: dataLayerType;
      isModel?: false | undefined;
      modelName?: string;
      sectionName?: string;
    }
  | {
      datalayer?: dataLayerType;
      isModel: true;
      modelName?: string;
      sectionName?: string;
    };

export const useDCADynamicModelAlloy = (mboxValue?: string) => {
  const [isTargetContentLoading, setisTargetContentLoading] = useState(mboxValue ? true : false);
  const { datalayer } = useSession();

  // set the Target Response
  const [isTargetData, setisTargetData] = useState<any>('');

  /**
   * makes the call to the Adobe Target if mboxParam value is authored
   */
  useEffect(() => {
    if (!isEmpty(datalayer)) {
      mboxValue && fetchAndUpdateTargetData(mboxValue);
    }
  }, [datalayer]);

  if (!mboxValue) {
    /**
     * If mbox parameter is not present isTargetContentLoading is false
     * and component behavior is not effected by target.
     */
    return { isTargetContentLoading: false, targetData: '' };
  }

  /**
   *
   * @param mboxValue | Makes the call to target based on the mboxParameter and fetches the respective response from target
   * @returns | Target Data will be returned
   */
  const fetchAndUpdateTargetData = async (mboxValue?: string) => {
    const mboxParameter = mboxValue && [mboxValue];
    try {
      const result: ResultType = await window?.alloy('sendEvent', {
        data: {
          dataLayerInfo: datalayer,
        }, // pass object to be added in datalayer
        xdm: {},
        decisionScopes: mboxParameter,
      });

      const propositions = result?.propositions;

      if (propositions && mboxValue) {
        for (const proposition of propositions) {
          const item = proposition?.items?.find(
            (item: { data: { content: { mboxParameter: string } } }) => item?.data?.content?.mboxParameter === mboxValue
          );
          if (item) {
            setisTargetData(item?.data?.content);
          }
        }

        setisTargetContentLoading(true);
        return {
          isTargetContentLoading: isTargetContentLoading,
          targetData: isTargetData,
        };
      }
    } catch (error) {
      console.log('Error occurred in Target call=====', error);
      return { isTargetContentLoading: false, targetData: {} };
    }
    return { isTargetContentLoading: isTargetContentLoading, targetData: isTargetData };
  };

  return { isTargetContentLoading: isTargetContentLoading, targetData: isTargetData };
};

/**
 * @returns | if the dir is rtl then returns true else false.
 */
export const getDirection = (() => {
  let cached: boolean | null = null;
  return () => {
    if (cached !== null) return cached;
    if (typeof document !== 'undefined') {
      const directionValue = document?.documentElement?.getAttribute('dir');
      cached = directionValue === 'rtl';
      return cached;
    }
    cached = false;
    return cached;
  };
})();

// Default delay of 200ms if not provided
export const debounce = (func: any, delay: number = 200) => {
  let timeout: number | NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

// Helper function to toggle arrow states
export const toggleArrowState = (arrowEL: any, state: boolean) => {
  if (arrowEL) {
    if (state) {
      arrowEL?.classList?.add('is-disabled'); // Disable the arrow
      arrowEL?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
    } else {
      arrowEL?.classList?.remove('is-disabled'); // Enable the arrow
      arrowEL?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
    }
  }
};

/**
 *
 * @param s | filter name
 * @returns | last element after the / which is specific filter value ..
 */
export const filterValue = (s: string) => {
  return s?.split('/')?.pop();
};

/**
 * return's current suite's based on the active Filter's...
 */
export const getCurrentSuites = (data: any, suitesInfo: any) => {
  const activeFilters = data?.filter((f: { dimensions: string | any[] }) => f?.dimensions && f?.dimensions?.length > 0);

  /**
   * If there are no active Filter's return's original all suites...
   */
  if (activeFilters?.length === 0) return suitesInfo;

  /**
   * Else return's the filtered suites from the active Filters
   */
  return suitesInfo?.filter((suite: { tags: any[] }) => {
    return activeFilters?.every((filter: { type: string; dimensions: (string | undefined)[] }) => {
      if (filter?.type === FILTER_TYPES.BRANDS) {
        return suite?.tags?.some((tag: string) => {
          const brandCode = filterValue(tag);
          return filter?.dimensions?.includes(brandCode);
        });
      } else {
        return filter?.dimensions?.some((dimension: any) =>
          suite?.tags?.some((tag: string | any[]) => tag?.includes(dimension))
        );
      }
    });
  });
};

/**
 *
 * @param originalFiltersData | all filter's data
 * @param selectedFilters | selected filter's data
 * @param allSuites | all suite's data
 * @returns | updated filter's by checking the suite's & all filter's ...
 */

export const getUpdatedFiltersData = (originalFiltersData: any[], selectedFilters: any[], allSuites: any[]) => {
  // Get active filter sections from selected filter's
  const activeSections = new Set(
    selectedFilters
      ?.filter(filter => {
        // For BedRooms, ignore if "any" is present in dimensions
        if (filter?.type === FILTER_TYPES.BEDROOMS) {
          return (
            filter?.dimensions &&
            filter?.dimensions?.length > 0 &&
            !filter?.dimensions?.includes(FILTER_TYPES.ANY_BEDROOMS)
          );
        }
        return filter?.dimensions && filter?.dimensions?.length > 0;
      })
      ?.map(filter => filter?.type)
  );

  /**
   * return's current suite's based on the selected Filter's...
   */
  const currentSuites = getCurrentSuites(selectedFilters, allSuites);

  /**
   * Filtering the filter's from the all filter's to show case only the applicable filter's for user interaction
   */
  return originalFiltersData?.map(filterSection => {
    const sectionName = filterSection?.type;
    const hasActiveSelections = activeSections?.has(sectionName);
    if (hasActiveSelections && activeSections?.size === 1) {
      /**
       * if activeSections size is 1 then doesn't filter that particular section but filter the rest....
       */
      return {
        ...filterSection,
      };
    } else {
      if (filterSection?.type === FILTER_TYPES.BRANDS) {
        /**
         * constructing the brand's filter's based on the filterSelection's
         */
        return {
          ...filterSection,
          data: filterSection?.data?.map((categoryData: { brands: any[] }) => ({
            ...categoryData,
            brands: categoryData?.brands?.map((brand: { isDisabled: boolean; brandTagId: string }) => {
              /**
               * Checking the Brand Tag if it's present on the filtered suite's or not
               * if yes return's true and else false to set isDisabled flag...
               */

              // Check if non-brand sections have active selections
              const hasNonBrandSelections = Array.from(activeSections).some(section => section !== FILTER_TYPES.BRANDS);

              /**
               * Get current suites based on NON-BRAND filters only (for brand availability calculation)
               */
              const nonBrandFilters = selectedFilters?.filter(filter => filter?.type !== FILTER_TYPES.BRANDS);
              const suitesFilteredByNonBrands = getCurrentSuites(nonBrandFilters, allSuites);

              let isAvailable = true;
              if (hasNonBrandSelections) {
                // Only check availability against non-brand filtered suites
                isAvailable = suitesFilteredByNonBrands?.some((suite: { tags: string[] }) =>
                  suite?.tags?.some((tag: string) => {
                    const brandCode = filterValue(tag);
                    return brand?.brandTagId === brandCode;
                  })
                );
              }

              /**
               *  Brands are only disabled if non-brand filters make them unavailable
               *  also if there were no suite's availble
               */
              return {
                ...brand,
                isDisabled:
                  currentSuites?.length === 0 ? true : hasNonBrandSelections ? !isAvailable : brand?.isDisabled,
              };
            }),
          })),
        };
      } else if (filterSection?.type === FILTER_TYPES.COUNTRY) {
        const nonLocFilters = selectedFilters?.filter(filter => filter?.type !== FILTER_TYPES.COUNTRY);
        const prevSuites = getCurrentSuites(nonLocFilters, allSuites);
        const allAvailableFilters = filterSection?.data?.filter((filterItem: { code: string }) => {
          return prevSuites?.some((suite: { tags: (string | any[])[] }) =>
            suite?.tags?.some((tag: string | any[]) => tag?.includes(filterItem?.code))
          );
        });
        return {
          ...filterSection,
          data: allAvailableFilters?.map((filterItem: any) => {
            return {
              ...filterItem,
            };
          }),
        };
      } else {
        /**
         * filtering the other available filter's from the filtered suite's ...
         */
        const allAvailableFilters = filterSection?.data?.filter((filterItem: { code: string }) => {
          return currentSuites?.some((suite: { tags: (string | any[])[] }) =>
            suite?.tags?.some((tag: string | any[]) => tag?.includes(filterItem?.code))
          );
        });

        //constructing the other available filter's...
        return {
          ...filterSection,
          data: allAvailableFilters?.map((filterItem: any) => {
            return {
              ...filterItem,
            };
          }),
        };
      }
    }
  });
};

/**
 *
 * @param payload | Filter's Selection Data
 * @returns | Total filter's selected count leaving bedroom's any.
 */
export const totalFilterCount = (payload: any, brandsClicked = false, props: any = null) => {
  return (
    payload?.reduce((count: number, obj: any) => {
      const dimensions = obj?.dimensions || [];

      if (obj?.type === FILTER_TYPES?.BEDROOMS) {
        // For bedrooms, don't count 'any' as a selection
        const specificBedroomFilters = dimensions?.filter((tag: string) => tag !== FILTER_TYPES.ANY_BEDROOMS);
        return count + specificBedroomFilters?.length;
      } else {
        // For other filters, count all dimensions
        if (props && props?.propertyTypeTags?.length > 0 && obj?.type === 'PROPERTY_TYPES') {
          return count + dimensions?.length - props?.propertyTypeTags?.length;
        } else if (props && props?.amenityTags?.length > 0 && obj?.type === 'AMENITIES') {
          return count + dimensions?.length - props?.amenityTags?.length;
        } else if (props && props?.activityTags?.length > 0 && obj?.type === 'ACTIVITIES') {
          return count + dimensions?.length - props?.activityTags?.length;
        } else if (
          obj?.type !== 'LEISURE_REGIONS' &&
          (obj?.type !== 'BRANDS' ||
            (obj?.type === 'BRANDS' && brandsClicked) ||
            (obj?.type === 'BRANDS' && props?.brandTags?.length === 0))
        ) {
          return count + dimensions?.length;
        }
        return count;
      }
    }, 0) || 0
  );
};

/**
 * The function shuffles the obj and returns the shuffled array of cards
 */
export const shuffleCards = (cards: any) => {
  //shuffling the values of the obj
  const shuffledCards: any = Object.values(cards);
  for (let i = shuffledCards?.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    /**
     * if items exists in the model then only swap that array
     */
    [shuffledCards[i], shuffledCards[j]] = [shuffledCards[j], shuffledCards[i]];
  }
  return shuffledCards;
};

export const getSubstringAfterFirst = (str: string, separator = '/') => {
  const firstIndex = str.indexOf(separator);
  if (firstIndex !== -1) {
    return str.substring(firstIndex + separator.length);
  }
  return str;
};

// function to update the URL parameters based on the number of filters selected
export function updateURLParams(
  filterName: string,
  filterVal: any,
  brandPillClicked = false,
  isMap = false,
  isGeoQuery = false,
  mapCenter = { lat: 0, lng: 0 }
) {
  const params = new URLSearchParams(window?.location?.search ?? '');
  if (params?.has(filterName)) {
    params?.delete(filterName);
  } else if (!isGeoQuery) {
    params?.delete('latitude');
    params?.delete('longitude');
  }

  let correctURL = '';
  if (isGeoQuery && filterName === 'region') {
    if (mapCenter.lat !== Number(params?.get('latitude')) || mapCenter.lng !== Number(params?.get('longitude'))) {
      params?.delete('latitude');
      params?.delete('longitude');
      correctURL = `?${params}&latitude=${mapCenter.lat}&longitude=${mapCenter.lng}`;
    }
  } else if (filterVal && filterVal?.length !== 0) {
    if (filterName === 'connectingRooms') {
      correctURL = `?${params}&${filterName}=true`;
    } else if (filterName === 'marriottBrands' && isMap) {
      if (brandPillClicked) {
        correctURL = `?${params}&${filterName}=${filterVal}`;
      } else {
        correctURL = `?${params}`;
      }
    } else {
      correctURL = `?${params}&${filterName}=${filterVal}`;
    }
  } else {
    correctURL = `?${params}`;
  }

  window.history.replaceState({}, '', decodeURIComponent(correctURL));
}

export const constructPayloadFromUrl = (url: string, filtersPayload: any, parameterMap: any, isMap = false) => {
  const params = new URLSearchParams(url);
  const payload = filtersPayload;

  for (const [key, configParam] of Object.entries(parameterMap)) {
    if (params?.has(key)) {
      const paramValue: any = params?.get(key);
      const config: any = configParam;
      const filterItem = payload?.find((item: any) => item?.type === config?.type);

      if (filterItem) {
        if (paramValue.includes(',')) {
          // Handle comma-separated values
          const codes = paramValue?.split(',');
          !isMap && (filterItem.codes = codes);
          filterItem.dimensions = codes?.map((code: any) => config?.dimensionPrefix + code);
        } else {
          // Handle single values and mappings
          const code = config?.valueMap?.[paramValue] || paramValue;
          !isMap && (filterItem.codes = [code]);
          filterItem.dimensions = [config?.dimensionPrefix + code];
        }

        // Clean up any default dimensions
        if (filterItem.type === FILTER_TYPES.BEDROOMS) {
          filterItem.dimensions = filterItem?.dimensions?.filter((d: string) => d !== FILTER_TYPES.ANY_BEDROOMS);
        }
      }
    }
  }

  return payload;
};

export function getViewportValue(): string {
  if (typeof window === 'undefined') return '';
  const smallMobile = window.matchMedia('(max-width: 370px)');
  const xs = window.matchMedia('(max-width: 767px)');
  const lg = window.matchMedia('(min-width: 992px)');
  if (xs?.matches) {
    if (smallMobile?.matches) return 'Small Mobile';
    return 'Mobile';
  }
  if (lg?.matches) return 'viewPortL';
  return 'viewPortM';
}

export function useViewport(): any {
  const [viewPort, setViewPort] = useState(getViewportValue);
  const handleResize = useCallback(() => {
    setViewPort(prev => {
      const next = getViewportValue();
      return prev === next ? prev : next;
    });
  }, []);
  useLayoutEffect(() => {
    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);

    window.addEventListener('resize', debouncedResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return viewPort;
}

/**
 * forming the Map pin's || Cluster's URL..
 */
export const dataUrl = (svg: string) => {
  const encodedSvg = btoa(svg);
  const dataUrl = 'data:image/svg+xml;base64,' + encodedSvg;

  return dataUrl;
};

export const fetchHDData = async (client: any, searchQuery: any, inputValues: any) => {
  try {
    const { data } = await client.query({
      query: searchQuery,
      ...inputValues,
    });
    return data;
  } catch (error) {
    console.error('***Failed to fetch map data:', error);
    return null;
  }
};

export const mapActivitiesToBrandsStructure = (activities: ActivitiesInput, activityTag: string[]) => {
  // If no activities or categories are present, return a default structure
  if (!activities || !activities?.activityCategories) {
    return [];
  }

  // Use a Set for efficient lookup of the activity tags
  const activityTags = new Set(activityTag);

  const brandCategories = activities?.activityCategories?.map(category => {
    // Filter out items whose codes are in the activityTags set
    const filteredActivities = category?.activitiesList?.filter(item => !activityTags.has(item.code));

    // Map the filtered activities to the desired brand structure
    const brandsLabelListItems = filteredActivities?.map(item => ({
      brandTagId: item?.code,
      brandTagTitle: item?.label,
      icon: item?.icon,
    }));

    return {
      brandsLabelListItems,
      categoryTitle: category?.categoryTitle || activities?.activitiesLabel,
    };
  });

  return brandCategories;
};

export const updateFiltersPayload = (data: any, isGeoQuery = false, defaultRegion = '') => {
  const regionDefault = isGeoQuery
    ? []
    : [(defaultRegion ? defaultRegion : data?.regions?.fallbackRegion) || 'united-states'];

  const filtersPayload = [
    {
      type: 'BRANDS',
      dimensions: [...(data?.brandTags || [])],
    },
    {
      type: 'AMENITIES',
      dimensions: [...(data?.amenityTags || [])],
    },
    {
      type: 'PROPERTY_TYPES',
      dimensions: [...(data?.propertyTypeTags || [])],
    },
    {
      type: 'ACTIVITIES',
      dimensions: [...(data?.activityTags || [])],
    },
    {
      type: 'CITIES',
      dimensions: [],
    },
    {
      type: 'STATES',
      dimensions: [],
    },
    {
      type: 'COUNTRIES',
      dimensions: [],
    },
    {
      type: 'LEISURE_REGIONS',
      dimensions: regionDefault,
    },
  ];

  return filtersPayload;
};

/**
 * Compares the buckets with the filters list and returns the buckets
 */
export const filterAvailableFilters = (buckets: any[], filterTags: string[], filtersList = []) => {
  const filtersMap = new Map();
  const bucketsMap = new Map();
  const filters = filtersList && filtersList?.length > 0 ? filtersList : buckets;
  const remainingFilters =
    filterTags?.length > 0 ? filters?.filter((amenity: any) => !filterTags?.includes(amenity?.code)) : filters;

  remainingFilters?.forEach((amenity: { code: string; icon: string }) => {
    filtersMap?.set(amenity?.code, amenity?.icon);
  });

  buckets?.forEach((amenity: { code: string; count: number; label: string; description: string }) => {
    bucketsMap?.set(amenity?.code, { count: amenity.count, label: amenity.label, description: amenity.description });
  });
  // Create a new object to avoid mutating the original data
  // Map over the remaing filters and add the icon.
  const commonFilters: any = remainingFilters?.map((filter: { code: string; label: string }) => {
    //check if match is found so it can be enabled or disabled
    const hasMatch = bucketsMap?.has(filter?.code);
    const bucketsData = bucketsMap?.get(filter?.code);
    return {
      ...filter,
      iconClass: filtersMap?.get(filter?.code) ?? '',
      disabled: !hasMatch,
      count: bucketsData?.count,
      label: bucketsData?.label ? bucketsData?.label : filter?.label,
      description: bucketsData?.description,
    };
  });

  // Enabled first (disabled=false), Disabled after (disabled=true)
  const sortedCommonFilters = commonFilters?.slice().sort((a: any, b: any) => Number(a.disabled) - Number(b.disabled));

  return sortedCommonFilters || [];
};

const filterAvailable = (hasFilterTags: boolean, filterSelected: boolean, payload: any, id: string) => {
  if (hasFilterTags) {
    if (filterSelected) {
      return true;
    } else {
      return false;
    }
  } else {
    return payload?.type === id && payload?.dimensions?.length > 0;
  }
};

export const FiltersPillSelection = (payloads: any, id: string, filterProps?: any, brandsClicked: boolean = false) => {
  return payloads?.some((payload: { type: string; dimensions: string[] }) => {
    if (payload?.type === FILTER_TYPES.BEDROOMS) {
      if (!payload?.dimensions?.includes(FILTER_TYPES.ANY_BEDROOMS)) {
        return payload?.type === id && payload?.dimensions?.length > 0;
      } else {
        return payload?.type === id && payload?.dimensions?.length > 1;
      }
    } else if (payload?.type === FILTERS_MAPPING.Brands && id === FILTERS_MAPPING.Brands) {
      const hasBrandTags = filterProps?.brandTags?.length > 0;

      if ((brandsClicked && hasBrandTags) || !hasBrandTags) {
        return payload?.type === id && payload?.dimensions?.length > 0;
      } else {
        return false;
      }
    } else if (payload?.type === FILTERS_MAPPING.Amenities && id === FILTERS_MAPPING.Amenities) {
      const hasAmenityTags = filterProps?.amenityTags?.length > 0;
      const filterSelected = filterProps?.amenityTags?.length !== payload?.dimensions?.length;

      return filterAvailable(hasAmenityTags, filterSelected, payload, id);
    } else if (payload?.type === FILTERS_MAPPING.Property && id === FILTERS_MAPPING.Property) {
      const hasPropertyTags = filterProps?.propertyTypeTags?.length > 0;
      const filterSelected = filterProps?.propertyTypeTags?.length !== payload?.dimensions?.length;

      return filterAvailable(hasPropertyTags, filterSelected, payload, id);
    } else if (payload?.type === FILTERS_MAPPING.Activities && id === FILTERS_MAPPING.Activities) {
      const hasActivityTags = filterProps?.activityTags?.length > 0;
      const filterSelected = filterProps?.activityTags?.length !== payload?.dimensions?.length;

      return filterAvailable(hasActivityTags, filterSelected, payload, id);
    } else if (payload?.type === FILTERS_MAPPING.Regions && id === FILTERS_MAPPING.Regions) {
      return false;
    } else if (
      (payload?.type === FILTERS_MAPPING?.City ||
        payload?.type === FILTERS_MAPPING?.State ||
        payload?.type === FILTERS_MAPPING?.Country) &&
      id === FILTERS_MAPPING.Location
    ) {
      return payload?.dimensions?.length > 0;
    } else {
      return payload?.type === id && payload?.dimensions?.length > 0;
    }
  });
};

export const filterPillAvailable = (facets: any[], filterType: string) => {
  if (filterType === 'LEISURE_REGIONS' || filterType === 'LOCATION') return true;

  const buckets =
    facets?.find((filter: { type: { enumCode: string } }) => filter?.type?.enumCode === filterType)?.buckets?.length >
    0;

  return buckets ?? false;
};

/**
 * @param isAuthorMode - When true (AEM author), missing `show*` flags keep filters visible unless explicitly false.
 *                       When false (publish / localhost non-author), missing flags hide filters (original behavior).
 */
export const getFilteredOrder = (flags: any, filterOrder: any, isAuthorMode = false) => {
  const filterMap: any = isAuthorMode
    ? {
        LEISURE_REGIONS: true,
        LOCATION: true,
        ACTIVITIES: flags?.showActivities !== false,
        BRANDS: flags?.showBrands !== false,
        AMENITIES: flags?.showAmenities !== false,
        PROPERTY_TYPES: flags?.showPropertyType !== false,
      }
    : {
        LEISURE_REGIONS: true,
        LOCATION: true,
        ACTIVITIES: flags?.showActivities || false,
        BRANDS: flags?.showBrands || false,
        AMENITIES: flags?.showAmenities || false,
        PROPERTY_TYPES: flags?.showPropertyType || false,
      };

  return filterOrder?.filter((filter: any) => {
    const filterKey = filter?.filterName;
    return filterMap[filterKey] !== false;
  });
};

export const getDescriptionByCode = (regions: any, defaultValue: string, isRegionPill = '') => {
  if (isRegionPill) return isRegionPill;

  for (const item of regions) {
    if (item?.buckets && Array?.isArray(item?.buckets)) {
      const foundBucket = item?.buckets?.find((bucket: { code: string }) => bucket?.code === defaultValue);
      if (foundBucket) {
        return foundBucket?.description;
      }
    }
  }
  return '';
};

/**
 * Builds the Location dropdown facet list (CITIES / STATES / COUNTRIES) while removing any option
 * whose label or description exactly matches a LEISURE_REGIONS bucket label or description,
 * so users do not see the same place twice (e.g. "United States" as both a region and a country).
 *
 * Deduping is intentionally label/description-only — code-based matching is avoided because
 * continent-level region codes do not correspond to country codes and would cause country entries
 * to be incorrectly hidden from the Location dropdown.
 *
 * @param filtersFacets - Full UXL facet array from map/location search (includes LEISURE_REGIONS + location types).
 * @returns Only location-type facets, each with overlapping buckets filtered out; empty array if input is empty.
 */
export const excludeRegionBucketsFromLocationFacets = (filtersFacets: any[] | undefined): any[] => {
  if (!filtersFacets?.length) return [];

  const regionFacets = filtersFacets?.filter(
    (filterObj: { type?: { enumCode?: string } }) => FILTERS_MAPPING.Regions === filterObj?.type?.enumCode
  );

  const regionBuckets = regionFacets?.flatMap((facet: any) => facet?.buckets ?? []);

  const regionLabelSet = new Set<string>();
  regionBuckets.forEach((b: { label?: string; description?: string }) => {
    if (b?.label) regionLabelSet.add(String(b.label).trim().toLowerCase());
    if (b?.description) regionLabelSet.add(String(b.description).trim().toLowerCase());
  });

  /**
   * Returns true if this CITIES/STATES/COUNTRIES bucket should be hidden from the Location UI
   * because its label or description duplicates an entry in the Regions facet.
   */
  const shouldExcludeBucket = (bucket: { label?: string; description?: string }) => {
    const label = bucket?.label?.trim().toLowerCase();
    const desc = bucket?.description?.trim().toLowerCase();
    if (label && regionLabelSet.has(label)) return true;
    if (desc && regionLabelSet.has(desc)) return true;
    return false;
  };

  return filtersFacets
    .filter((filterObj: { type?: { enumCode?: string } }) => LOCATIONS.includes(filterObj?.type?.enumCode ?? ''))
    .map((filterObj: any) => ({
      ...filterObj,
      buckets: filterObj?.buckets?.filter((b: any) => !shouldExcludeBucket(b)) ?? [],
    }))
    .filter((filterObj: { buckets?: unknown[] }) => (filterObj?.buckets?.length ?? 0) > 0);
};

// Function to map filter codes to their display labels using facets
export const getDisplayedFilters = (
  payload: { type: string; dimensions: string[] }[],
  availableFacets: any,
  brandsClicked: boolean,
  props: {
    amenityTags: string[];
    activityTags: string[];
    propertyTypeTags: string[];
    brandTags: string[];
  }
): DisplayedFilter[] => {
  const displayedFilters: DisplayedFilter[] = [];

  // Helper function to check if a code is in a given tag list
  const isfilterTagPresent = (type: string, code: string): boolean => {
    switch (type) {
      case FILTERS_MAPPING.Amenities:
        return !!props?.amenityTags?.includes(code);
      case FILTERS_MAPPING.Activities:
        return !!props?.activityTags?.includes(code);
      case FILTERS_MAPPING.Property:
        return !!props?.propertyTypeTags?.includes(code);
      default:
        return false;
    }
  };

  payload?.forEach(filterGroup => {
    const filterType = filterGroup?.type;
    const selectedCodes = filterGroup?.dimensions;

    if (
      filterType !== FILTERS_MAPPING.Regions &&
      (filterType !== FILTERS_MAPPING.Brands ||
        (filterType === FILTERS_MAPPING.Brands && brandsClicked) ||
        props?.brandTags?.length === 0)
    ) {
      const facetSource = availableFacets?.find(
        (facet: { type: { enumCode: string } }) => facet?.type?.enumCode === filterType
      );

      if (facetSource) {
        // Filter out options that are present in their corresponding tag list
        const otherFilterOptions = facetSource?.buckets?.filter(
          (opt: { code: string }) => !isfilterTagPresent(filterType, opt.code)
        );

        selectedCodes?.forEach(code => {
          const option = otherFilterOptions?.find((opt: { code: string }) => opt?.code === code);

          if (option) {
            displayedFilters?.push({
              id: `${filterType}-${option?.code}`,
              label: option?.label,
              type: filterType,
              code: option?.code,
            });
          }
        });
      }
    }
  });

  return displayedFilters;
};

//function to form cta url for hws
export const getCtaUrl = (node: any, isReview?: boolean) => {
  const brandId = node?.basicInformation?.brand?.id || node?.property?.basicInformation?.brand?.id;
  const seoNickname = node?.seoNickname || node?.property?.seoNickname;
  const subDirectoryVal = brandId === 'RZ' ? 'en' : 'en-us';
  const sectionVal = isReview ? 'reviews' : 'overview';

  return brandId === 'RZ'
    ? `https://www.ritzcarlton.com/${subDirectoryVal}/hotels/${seoNickname}/overview/`
    : `/${subDirectoryVal}/hotels/${seoNickname}/${sectionVal}/`;
};

/**
 *
 * @param count | number of parallel API calls
 * @param query | query to trigger
 * @param baseVariables | payload
 * @returns | response of the parallel calls in the form of combined array....
 */
export const parallelDynamicCalls = async (
  count: number,
  query: any,
  baseVariables: any,
  client: any,
  requestID: any,
  currentLocale: string
) => {
  // Create an array of 'n' length and mapping it to the API call...
  const apiRequests = Array.from({ length: count }, async (_, index: number) => {
    const offSet = PROPERTIES_LIMIT * (index + 1);

    const variables = {
      ...baseVariables,
      limit: PROPERTIES_LIMIT,
      offset: offSet,
    };

    return await fetchHDData(client, query, {
      variables,
      context: {
        headers: {
          'x-request-id': requestID,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    });
  });

  try {
    //Execute all calls in parallel
    const results = await Promise.all(apiRequests);
    return results;
  } catch (error) {
    return error;
  }
};

/**
 * Building a priority map using server-provided order.
 * - Skips LEISURE_REGIONS.
 * - Expands LOCATION to CITIES/STATES/COUNTRIES with same priority.
 */
export function buildFilterPriorityMap(filtersOrderList: any) {
  const map: Record<string, number> = {};
  let priority = 1;

  for (const item of filtersOrderList ?? []) {
    const name = item?.filterName;

    if (REGION_FILTER?.has(name)) continue;

    if (name === 'LOCATION') {
      for (const key of LOCATION_KEYS) map[key] = priority;
      priority += 1;
      continue;
    }

    // Handle BRANDS, ACTIVITIES, AMENITIES, PROPERTY_TYPES
    map[name] = priority;
    priority += 1;
  }

  return map;
}

export const ternaryHandler = (value: unknown, defaultValue: string | boolean | unknown = '') => {
  return value ?? defaultValue;
};

export function accountAgeCalculator(datalayer: dataLayerType) {
  if (datalayer?.['memState'] === 'authenticated') {
    const joinDate = (ternaryHandler(datalayer?.['mr_prof_join_date']) as string).replaceAll('-', '/');
    const joinDateObj = new Date(joinDate);
    const today = new Date();

    //calculate time difference in milliseconds
    const diffInMs: number = today.getTime() - joinDateObj.getTime();

    //convert ms -> days (1 day = 86400000ms)
    const diffInDays = Math.round(diffInMs / 86400000);

    //ensure it's valid number
    if (!isNaN(diffInDays)) {
      return diffInDays.toString();
    }
    return '';
  }
  return '';
}

export const updateDateFormat = (input: string, format = 'MM/DD/YYYY') => {
  if (input === '') {
    return '';
  }
  const parsedDate = dayjs(input);
  if (!parsedDate.isValid()) {
    return '';
  }
  return parsedDate.format(format);
};

export function sanitizeObject(obj: dataLayerType) {
  if (typeof obj !== 'object' || obj === null) return obj;

  const cleanedObj: { [key: string]: unknown } = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === '' || value === null || value === undefined) {
      continue; // Skip keys with "", null, or undefined
    }
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const nested = sanitizeObject(value as dataLayerType);
      // Only add if nested object is not empty
      if (Object.keys(nested ?? {}).length > 0) {
        cleanedObj[key] = nested;
      }
    } else {
      cleanedObj[key] = value;
    }
  }

  return cleanedObj;
}
